import { useState, useRef, useEffect, useCallback, type KeyboardEvent } from "react"
import { createPortal } from "react-dom"
import { ChevronDown, Check } from "lucide-react"

export type GlitchSelectOption = {
  value: string
  label: string
}

type GlitchSelectProps = {
  options: GlitchSelectOption[]
  value: string | null
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  "aria-label"?: string
  className?: string
  /** Icon to show before the selected label. */
  icon?: React.ReactNode
}

export function GlitchSelect({
  options,
  value,
  onChange,
  placeholder = "Select…",
  disabled = false,
  "aria-label": ariaLabel,
  className,
  icon,
}: GlitchSelectProps) {
  const [open, setOpen] = useState(false)
  const [focusIdx, setFocusIdx] = useState(-1)
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({})
  const containerRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handle = (e: MouseEvent) => {
      const t = e.target as Node
      if (
        containerRef.current && t && !containerRef.current.contains(t) &&
        listRef.current && !listRef.current.contains(t)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener("click", handle, true)
    return () => document.removeEventListener("click", handle, true)
  }, [open])

  // Position dropdown
  useEffect(() => {
    if (!open || !containerRef.current) return
    const update = () => {
      if (!containerRef.current) return
      const rect = containerRef.current.getBoundingClientRect()
      const spaceBelow = window.innerHeight - rect.bottom
      const openAbove = spaceBelow < 200 && rect.top > spaceBelow
      setDropdownStyle({
        position: "fixed",
        left: rect.left,
        width: Math.max(rect.width, 180),
        maxWidth: Math.min(window.innerWidth - 16, 384),
        ...(openAbove
          ? { bottom: window.innerHeight - rect.top + 4, top: "auto" }
          : { top: rect.bottom + 4, bottom: "auto" }),
        zIndex: 10001,
      })
    }
    update()
    window.addEventListener("scroll", update, true)
    window.addEventListener("resize", update)
    return () => {
      window.removeEventListener("scroll", update, true)
      window.removeEventListener("resize", update)
    }
  }, [open])

  // Reset focus index when opening
  useEffect(() => {
    if (open) {
      const idx = options.findIndex((o) => o.value === value)
      setFocusIdx(idx >= 0 ? idx : 0)
    }
  }, [open, options, value])

  // Scroll focused item into view
  useEffect(() => {
    if (!open || !listRef.current || focusIdx < 0) return
    const el = listRef.current.children[focusIdx] as HTMLElement | undefined
    el?.scrollIntoView({ block: "nearest" })
  }, [open, focusIdx])

  const select = useCallback((val: string) => {
    onChange(val)
    setOpen(false)
  }, [onChange])

  const handleKeyDown = (e: KeyboardEvent) => {
    if (disabled) return
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter" || e.key === " ") {
        e.preventDefault()
        setOpen(true)
      }
      return
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault()
        setFocusIdx((i) => Math.min(i + 1, options.length - 1))
        break
      case "ArrowUp":
        e.preventDefault()
        setFocusIdx((i) => Math.max(i - 1, 0))
        break
      case "Enter":
      case " ":
        e.preventDefault()
        if (focusIdx >= 0 && focusIdx < options.length) {
          select(options[focusIdx].value)
        }
        break
      case "Escape":
        e.preventDefault()
        setOpen(false)
        break
      case "Tab":
        setOpen(false)
        break
    }
  }

  const selectedLabel = options.find((o) => o.value === value)?.label ?? placeholder

  return (
    <div ref={containerRef} className={`relative ${className ?? ""}`}>
      <button
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-xl border border-white/[0.08] bg-white/[0.04] text-white/80 text-left font-mono text-[11px] focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-400/30 disabled:opacity-50 transition-colors hover:border-white/[0.12]"
      >
        {icon && <span className="shrink-0 text-white/30">{icon}</span>}
        <span className={`truncate flex-1 ${value == null ? "text-white/40" : ""}`}>
          {selectedLabel}
        </span>
        <ChevronDown
          className={`w-3.5 h-3.5 shrink-0 text-white/30 transition-transform duration-150 ${open ? "rotate-180" : ""}`}
          aria-hidden
        />
      </button>

      {open && createPortal(
        <div
          ref={listRef}
          role="listbox"
          aria-label={ariaLabel}
          style={dropdownStyle}
          className="max-h-[280px] overflow-auto rounded-xl border border-white/[0.08] bg-black/90 backdrop-blur-2xl shadow-2xl py-1"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {options.map((opt, i) => {
            const isSelected = opt.value === value
            const isFocused = i === focusIdx
            return (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  select(opt.value)
                }}
                onMouseEnter={() => setFocusIdx(i)}
                className={`w-full text-left px-3 py-2 font-mono text-[11px] flex items-center gap-2 transition-colors focus:outline-none ${
                  isFocused ? "bg-white/[0.06] text-white" : "text-white/70 hover:bg-white/[0.04]"
                }`}
              >
                <span className="truncate flex-1">{opt.label}</span>
                {isSelected && <Check className="w-3 h-3 shrink-0 text-teal-400" aria-hidden />}
              </button>
            )
          })}
          {options.length === 0 && (
            <div className="px-3 py-3 font-mono text-[11px] text-white/30 text-center">
              No options
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  )
}
